import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck,
  Package,
  MapPin,
  Phone,
  Mail,
  User,
  Home,
  Building,
  Navigation,
  ArrowRight,
  CheckCircle,
  Clock,
  Star,
  History,
  Plus,
  ChevronRight,
  Sparkles,
  Calendar,
} from "lucide-react";

// Step Indicator Component
function StepIndicator({ currentStep }) {
  const steps = [
    { icon: CheckCircle, label: "Order Placed" },
    { icon: Package, label: "Packaging" },
    { icon: Truck, label: "Shipped" },
    { icon: Navigation, label: "Out for Delivery" },
  ];

  return (
    <div className="flex items-center justify-between">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = index <= currentStep;
        const isCurrent = index === currentStep;

        return (
          <React.Fragment key={index}>
            <div className="flex flex-col items-center">
              <motion.div
                animate={{
                  scale: isCurrent ? [1, 1.1, 1] : 1,
                  backgroundColor: isActive ? "#3b82f6" : "#e5e7eb",
                }}
                transition={{ repeat: Infinity, duration: 2, repeatType: "reverse" }}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isActive ? "text-white" : "text-gray-400"
                }`}
              >
                <Icon size={24} />
              </motion.div>
              <p className={`text-xs mt-2 font-medium ${isActive ? "text-gray-800" : "text-gray-400"}`}>
                {step.label}
              </p>
            </div>
            {index < steps.length - 1 && (
              <div className="flex-1 h-1 mx-2 bg-gray-200 rounded-full">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-500"
                  style={{ width: index < currentStep ? "100%" : "0%" }}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Shipping Form Input
function FormInput({ icon: Icon, placeholder, type = "text", className = "" }) {
  return (
    <div className="relative">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400">
        <Icon size={20} />
      </div>
      <input
        type={type}
        placeholder={placeholder}
        className={`w-full pl-16 pr-4 py-4 bg-white border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all ${className}`}
      />
    </div>
  );
}

// Shipping History Card
function HistoryCard({ item }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
            <Package size={24} />
          </div>
          <div>
            <h4 className="font-semibold text-gray-800">{item.trackingId}</h4>
            <p className="text-sm text-gray-500">{item.destination}</p>
          </div>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <Clock size={12} />
            {item.status}
          </span>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Calendar size={14} />
          {item.date}
        </div>
        <button className="flex items-center gap-1 text-blue-600 text-sm font-medium hover:text-blue-700">
          Track Package <ChevronRight size={16} />
        </button>
      </div>
    </motion.div>
  );
}

const Shipping = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [shippingData, setShippingData] = useState({
    fullName: "",
    phone: "",
    email: "",
    street: "",
    city: "",
    state: "",
    postalCode: "",
  });

  const shippingHistory = [
    { trackingId: "SHP-2024-001", destination: "Mumbai, Maharashtra", status: "Delivered", date: "Jan 15, 2024" },
    { trackingId: "SHP-2024-002", destination: "Delhi, NCR", status: "In Transit", date: "Jan 18, 2024" },
    { trackingId: "SHP-2024-003", destination: "Bangalore, Karnataka", status: "Processing", date: "Jan 20, 2024" },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Shipping details saved!");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Shipping</h1>
            <p className="text-gray-500">Manage your shipping details and track packages</p>
          </div>
        </motion.div>

        {/* Hero Section with Stepper */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 mb-8 overflow-hidden"
        >
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white">Track Your Shipment</h2>
                <p className="text-gray-400 mt-1">Real-time updates on your delivery</p>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-xl">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                <span className="text-white text-sm font-medium">Live Tracking</span>
              </div>
            </div>

            {/* Step Indicator */}
            <StepIndicator currentStep={currentStep} />
          </div>
        </motion.div>

        {/* Shipping Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden mb-8"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <MapPin className="w-6 h-6" />
              Receiver Information
            </h2>
            <p className="text-white/80 text-sm mt-1">Enter the delivery details</p>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput icon={User} placeholder="Full Name" />
              <FormInput icon={Phone} placeholder="Phone Number" type="tel" />
              <FormInput icon={Mail} placeholder="Email Address" type="email" className="md:col-span-2" />
              <FormInput icon={Home} placeholder="Street Address" className="md:col-span-2" />
              <FormInput icon={Building} placeholder="City" />
              <FormInput icon={Navigation} placeholder="State" />
              <FormInput icon={MapPin} placeholder="Postal Code" className="md:col-span-2" />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full mt-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-lg shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <CheckCircle size={22} />
              Confirm Shipping
            </motion.button>
          </form>
        </motion.div>

        {/* Shipping History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <History className="w-6 h-6" />
              Shipping History
            </h2>
            <p className="text-white/80 text-sm mt-1">Your recent shipments</p>
          </div>

          <div className="p-8">
            {shippingHistory.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Package className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Shipping History</h3>
                <p className="text-gray-500">Your shipping records will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {shippingHistory.map((item, index) => (
                  <HistoryCard key={item.trackingId} item={item} />
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8"
        >
          {[
            { icon: Package, label: "Total Shipments", value: "156", color: "from-blue-500 to-cyan-500" },
            { icon: Truck, label: "In Transit", value: "12", color: "from-yellow-500 to-orange-500" },
            { icon: CheckCircle, label: "Delivered", value: "142", color: "from-green-500 to-emerald-500" },
            { icon: Star, label: "Avg Rating", value: "4.8", color: "from-purple-500 to-pink-500" },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Shipping;
